import React, { useState, useCallback } from "react";
import { TextField, Autocomplete, FilterOptionsState } from "@mui/material";
import { debounce } from "lodash";
import { useAddressValidationCache } from "./hooks/useAddressValidationCache";

interface AddressInputProps {
  value: string;
  onChange: (address: string) => void;
  disabled?: boolean;
}

export const AddressInput: React.FC<AddressInputProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [validatedAddress, setValidatedAddress] = useState<string | null>(null);

  const {
    validatedAddresses,
    validateAddress,
    findMatchingValidatedAddress,
    isValidAddress,
  } = useAddressValidationCache();

  const debouncedValidateAddress = useCallback(
    debounce((query: string) => validateAddress(query), 300),
    [validateAddress]
  );

  const handleInputChange = useCallback(
    (_: any, newValue: string) => {
      setInputValue(newValue);
      onChange(""); // Clear selected address when input changes
      setValidatedAddress(findMatchingValidatedAddress(newValue));
      debouncedValidateAddress(newValue);
    },
    [debouncedValidateAddress, findMatchingValidatedAddress, onChange]
  );

  const handleAddressChange = (_: any, newValue: string | null) => {
    if (newValue && isValidAddress(newValue)) {
      onChange(newValue);
      setValidatedAddress(newValue);
    }
  };

  const filterOptions = (
    options: string[],
    state: FilterOptionsState<string>
  ): string[] => {
    const trimmedInput = state.inputValue.trim();

    // If input exactly matches a validated address, show no options
    if (trimmedInput && isValidAddress(trimmedInput)) {
      return [];
    }

    // Otherwise show all validated addresses
    return options;
  };

  return (
    <Autocomplete
      // freeSolo
      options={validatedAddresses}
      filterOptions={filterOptions}
      value={value}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleAddressChange}
      isOptionEqualToValue={(option, value) => option === value}
      renderOption={(props, option) => <li {...props}>✓ {option}</li>}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus
          margin="dense"
          label="Address"
          fullWidth
          disabled={disabled}
          error={!!inputValue && !validatedAddress}
          helperText={
            !inputValue
              ? "Enter an address"
              : validatedAddress
                ? "Click validated address in dropdown to select"
                : "Invalid address"
          }
          data-testid="address-input"
        />
      )}
    />
  );
};
